// The two strips at the top of the dashboard: what the search is doing right
// now, and how fast it is doing it.
//
// **Above everything the digest carries, on purpose.** Below them the page is a
// report about generations that have landed — minutes apart, and nothing on it
// moves in between. These are the only parts that answer "is the search doing
// anything right now", so they go where that question is asked, and the first
// renders before the first digest has arrived: "connecting" over an empty page
// while the box plays four hundred games is the exact confusion the pulse was
// added to end.

import { styled, Text } from "../kit";
import type { ComputeView, PulseView } from "../tunerView";
import { Figure } from "./Figure";
import { TEAM_TONE } from "./palette";
import { Card, Lamp, Legend, Row } from "./parts";

/** One island's heartbeat. Drawn once per island, so a `styled`. */
const Beat = styled("span", {
  shouldForwardProp: (prop: string) => prop !== "moving"
})<{ moving?: boolean }>(({ theme, moving }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.kit.space.xs,
  padding: `2px ${theme.kit.space.sm}px`,
  borderRadius: theme.kit.radius.sm,
  border: `2px solid ${theme.kit.color.line}`,
  opacity: moving ? 1 : 0.55
}));

/** A beat lamp, smaller than the header's. */
const Dot = styled(Lamp)({ width: 6, height: 6 });

export const Figures = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.kit.space.xl
}));

export function LiveStrip({
  strip,
  stale
}: {
  strip: PulseView;
  stale: boolean;
}): React.ReactElement {
  return (
    <Card solid>
      <Row>
        <Lamp
          style={{
            background: stale
              ? TEAM_TONE.stock
              : strip.moving > 0
                ? TEAM_TONE.candidate
                : TEAM_TONE.draw
          }}
        />
        <Legend
          text={strip.headline}
          title="The heartbeats"
          rule="Read from the heartbeats every island writes as it plays, which is the smallest and fastest-moving thing the search produces. Everything below this line only changes when a generation lands, which is minutes apart."
        />
      </Row>
      {strip.islands.length > 0 && (
        <Row>
          {strip.islands.map((island) => (
            <Beat key={island.id} moving={island.moving}>
              <Dot
                style={{ background: island.moving ? TEAM_TONE.candidate : TEAM_TONE.draw }}
              />
              <Text tone="cream" size={11}>
                {island.id}
              </Text>
              <Text role="display" tone="dim" size={11}>
                {island.games}
              </Text>
            </Beat>
          ))}
        </Row>
      )}
    </Card>
  );
}

export function ComputeStrip({ compute }: { compute: ComputeView }): React.ReactElement {
  return (
    <Card solid>
      <Legend
        text={`How fast it is simulating · ${compute.over}`}
        title="Throughput"
        rule="Wall clock per game, read off the gallery records the islands write — so it is the fleet's own speed, since a pooled batch is timed on the runner that played it. The rate beside it comes from the heartbeats instead, because a rate is only worth showing if it is current."
      />
      <Figures>
        <Figure
          label="typical game"
          value={compute.typical}
          note="The median. Most games are short and the occasional one runs to the ply cap, so the mean sits where no game actually is."
        />
        <Figure
          label="mean game"
          value={compute.mean}
          note="What throughput is actually made of, which is why both travel."
        />
        <Figure
          label="a ply"
          value={compute.perPly}
          note="Pooled over the window. The figure that survives games getting shorter — a profile that starts winning faster plays fewer plies without the simulator being any quicker."
        />
        <Figure
          label="plies a game"
          value={String(compute.plies)}
          note="The context the durations need: a game twice as long should take twice as long."
        />
        {compute.perHour !== null && (
          <Figure
            label="games an hour"
            value={compute.perHour.toLocaleString()}
            note="Across the whole fleet, this pass, from the heartbeats — every game every island has played, not the sample the gallery keeps."
          />
        )}
      </Figures>
    </Card>
  );
}
